import { useEffect, useState, useMemo } from 'react';
import { T } from '../theme';
import { ContentHeader, Meter } from './fmxkit';
import { getRecentPushes, getAllExtractionRuns } from '../db';

// Dashboard — cross-project overview (FMX restyle, step 8).
// Stat tiles + "Migration progress" and "Recent imports" cards. All numbers
// derive from real data: projectRows comes precomputed from ProjectScreen
// (same progress rule as the grid), pushes/AI-spend are fetched here.

function StatTile({ v, k, sub }) {
  return (
    <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, padding: '14px 16px', flex: '1 1 160px' }}>
      <div style={{ font: `800 24px ${T.fontData}`, color: T.ink, letterSpacing: '-0.02em' }}>{v}</div>
      <div style={{ font: `600 11px ${T.fontSans}`, color: T.inkMuted, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 3 }}>{k}</div>
      {sub && <div style={{ font: `400 12px ${T.fontSans}`, color: T.inkSoft, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return `Today ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  }
  if (d.toDateString() === new Date(today.getTime() - 86400000).toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function DashboardScreen({ projectRows, importRows, projectNameById, onOpenProject }) {
  const [pushes, setPushes] = useState([]);
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getRecentPushes({ sinceDays: 30 }).then(p => { if (!cancelled) setPushes(p || []); });
    getAllExtractionRuns({ limit: 500 }).then(r => { if (!cancelled) setRuns(r || []); });
    return () => { cancelled = true; };
  }, []);

  const stats = useMemo(() => {
    const active = projectRows.filter(r => r.status === 'active').length;
    const setup = projectRows.filter(r => r.status === 'setup').length;
    const rowsPushed30 = pushes.reduce((s, p) => s + (p.succeeded ?? p.row_count ?? 0), 0);
    const weekAgo = Date.now() - 7 * 86400000;
    const importsThisWeek = importRows.filter(r => {
      const when = r.completed_at || r.updated_at;
      return when && new Date(when).getTime() >= weekAgo;
    }).length;
    const monthAgo = Date.now() - 30 * 86400000;
    const spend30 = runs
      .filter(r => r.created_at && new Date(r.created_at).getTime() >= monthAgo)
      .reduce((s, r) => s + (Number(r.estimated_cost_usd) || 0), 0);
    return { active, setup, rowsPushed30, importsThisWeek, spend30 };
  }, [projectRows, pushes, importRows, runs]);

  const recentImports = useMemo(() => {
    return [...importRows]
      .filter(r => r.completed_at || r.updated_at)
      .sort((a, b) => new Date(b.completed_at || b.updated_at) - new Date(a.completed_at || a.updated_at))
      .slice(0, 8);
  }, [importRows]);

  const cardTitle = { padding: '12px 16px', borderBottom: `1px solid ${T.borderSubtle}`, font: `700 14px ${T.fontSans}`, color: T.ink };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ContentHeader crumbs={['Dashboard']} title="Dashboard" subtitle="FMX Implementation · last 30 days" />
      <div style={{ flex: 1, overflow: 'auto', background: T.bg, padding: 20 }}>
        {/* Stat tiles */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
          <StatTile v={stats.active} k="Active projects" sub={stats.setup ? `${stats.setup} in setup` : undefined} />
          <StatTile v={stats.rowsPushed30.toLocaleString()} k="Rows pushed" sub="30 days" />
          <StatTile v={stats.importsThisWeek} k="Imports this week" />
          <StatTile v={`$${stats.spend30.toFixed(2)}`} k="AI spend" sub="30 days" />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-start' }}>
          {/* Migration progress */}
          <div style={{ flex: '1 1 380px', minWidth: 320, background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, overflow: 'hidden' }}>
            <div style={cardTitle}>Migration progress</div>
            {projectRows.length === 0 && (
              <div style={{ padding: '14px 16px', font: `400 13px ${T.fontSans}`, color: T.inkSoft, fontStyle: 'italic' }}>No projects yet.</div>
            )}
            {projectRows.map((r, i) => {
              const pct = r.progress.total > 0 ? Math.round((r.progress.completed / r.progress.total) * 100) : 0;
              return (
                <div
                  key={r.p.id}
                  onClick={() => onOpenProject && onOpenProject(r.p)}
                  style={{ padding: '12px 16px', borderBottom: i < projectRows.length - 1 ? `1px solid ${T.borderSubtle}` : 'none', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = T.bgMuted}
                  onMouseLeave={e => e.currentTarget.style.background = T.white}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 7 }}>
                    <span style={{ font: `600 13px ${T.fontSans}`, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.p.name}</span>
                    <span style={{ font: `600 12px ${T.fontData}`, color: pct === 100 ? T.greenText : T.inkMuted, whiteSpace: 'nowrap' }}>
                      {pct === 100 && <i className="fa-solid fa-circle-check" style={{ marginRight: 4 }} />}
                      {r.progress.completed}/{r.progress.total}
                    </span>
                  </div>
                  <Meter pct={pct} h={7} />
                </div>
              );
            })}
          </div>

          {/* Recent imports */}
          <div style={{ flex: '1 1 380px', minWidth: 320, background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, overflow: 'hidden' }}>
            <div style={cardTitle}>Recent imports</div>
            {recentImports.length === 0 && (
              <div style={{ padding: '14px 16px', font: `400 13px ${T.fontSans}`, color: T.inkSoft, fontStyle: 'italic' }}>No imports yet.</div>
            )}
            {recentImports.map((r, i) => (
              <div key={`${r.project_id}-${r.schema_type}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: i < recentImports.length - 1 ? `1px solid ${T.borderSubtle}` : 'none' }}>
                <span style={{ width: 28, height: 28, borderRadius: 6, background: T.blue50, color: T.brand, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="fa-solid fa-file-import" style={{ fontSize: 12 }} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: `600 13px ${T.fontSans}`, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.schema_type} <span style={{ color: T.inkSoft, fontWeight: 400 }}>· {projectNameById[r.project_id] || 'Unknown project'}</span>
                  </div>
                  <div style={{ font: `400 12px ${T.fontSans}`, color: T.inkSoft }}>{fmtWhen(r.completed_at || r.updated_at)}</div>
                </div>
                <span style={{ font: `600 13px ${T.fontData}`, color: T.ink }}>{((r.row_count_stored ?? r.row_count) || 0).toLocaleString()}</span>
                <span style={{ font: `400 12px ${T.fontSans}`, color: T.inkSoft }}>rows</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
